import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, Mail } from 'lucide-react';

import { useAuth } from '../auth/AuthProviderUnified';
import AuthLayout from '../components/auth/AuthLayout';
import { getFirebase } from '../firebase/lazy';
import { API_BASE_URL } from '../config/api.config';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage] = useState(() =>
    typeof location.state?.message === 'string' && location.state.message
      ? location.state.message
      : ''
  );
  const { user, login } = useAuth();
  const authStrategy = import.meta.env.VITE_AUTH_STRATEGY || 'jwt';

  const inputClass =
    'w-full px-4 py-3 bg-white border border-black/10 rounded-xl text-[#1C1C1E] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF6A00]/50 focus:border-[#FF6A00]/50 transition-colors';

  useEffect(() => {
    if (user) {
      // Redirect to unified Command Center dashboard
      navigate('/command-center');
    }
  }, [user, navigate]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      try {
        await login(email, password);
        setLoading(false);
        // Navigation handled by useEffect when user state changes
      } catch (err) {
        setLoading(false);
        // Parse error message for user-friendly display
        const errorMessage = err.message || 'Authentication failed';
        if (errorMessage.includes('verify your email') || errorMessage.includes('Email not verified')) {
          setError('Please verify your email address before logging in. Check your inbox for a verification link.');
        } else if (errorMessage.includes('Invalid') || errorMessage.includes('credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
          setError('Unable to connect to server. Please check your internet connection.');
        } else {
          setError(errorMessage);
        }
      }
    },
    [email, password, login]
  );

  const handleFormSubmit = useCallback(
    (e) => {
      handleSubmit(e);
    },
    [handleSubmit]
  );

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  return (
    <AuthLayout topRightText="Don't have an account?" topRightTo="/signup" topRightCta="Get started">
      <div className="relative w-full max-w-md">
        {/* Ambient brand glow behind the card */}
        <div className="absolute -inset-4 bg-[#FF6A00]/10 blur-3xl rounded-[2rem] opacity-60 pointer-events-none" />

        {/* Login Card */}
        <div className="relative bg-white vh-edge-light rounded-3xl p-8 space-y-6 overflow-hidden">
          <div className="relative">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center space-y-3 mb-8"
            >
              <h1 className="text-3xl font-bold text-[#1C1C1E] tracking-tight">Welcome back</h1>
              <p className="text-slate-600">Sign in to your Virtual Hardhat account</p>
            </motion.div>

            {/* Account-created / info message from navigators such as WorkerSignup */}
            {successMessage && !error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl shadow-lg"
                data-testid="login-success-message"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                      <span className="text-green-600 text-xs">✓</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{successMessage}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-lg"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center">
                      <span className="text-red-600 text-xs">!</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{error}</p>
                    {error.includes('verify your email') && (
                      <div className="mt-3">
                        <button
                          data-testid="resend-verification"
                          type="button"
                          onClick={async () => {
                            try {
                              if (authStrategy === 'firebase') {
                                const { auth, sendEmailVerification } = await getFirebase();
                                if (!auth.currentUser) {
                                  setError('Please sign in again to resend verification email.');
                                  return;
                                }
                                await sendEmailVerification(auth.currentUser, {
                                  url: `${window.location.origin}/verify-email`,
                                });
                                setError('Verification email sent! Please check your inbox.');
                                return;
                              }

                              const API_URL = API_BASE_URL;
                              const token = localStorage.getItem('vh_auth_token');
                              if (token) {
                                const response = await fetch(`${API_URL}/api/auth/send-verification-email`, {
                                  method: 'POST',
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'application/json',
                                  },
                                });
                                const data = await response.json();
                                if (response.ok) {
                                  setError('Verification email sent! Please check your inbox.');
                                } else {
                                  setError(data.error || data.message || 'Failed to send verification email');
                                }
                              }
                            } catch (err) {
                              setError('Failed to send verification email');
                            }
                          }}
                          className="text-sm text-[#FF6A00] hover:text-orange-300 font-semibold transition-colors underline"
                        >
                          Resend verification email
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Login Form */}
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="space-y-2"
              >
                <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Mail className="h-4 w-4 text-[#FF6A00]" />
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={handleEmailChange}
                  className={inputClass}
                  placeholder="you@company.com"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-2"
              >
                <label htmlFor="password" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Lock className="h-4 w-4 text-[#FF6A00]" />
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={handlePasswordChange}
                  className={inputClass}
                  placeholder="••••••••"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="flex justify-end"
              >
                <Link
                  to="/reset-password"
                  className="text-sm text-[#FF6A00] hover:text-orange-300 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF6A00]/50 rounded"
                >
                  Forgot password?
                </Link>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                type="submit"
                className="group relative w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 rounded-full shadow-[0_0_30px_rgba(255,106,0,0.35)] hover:shadow-[0_0_45px_rgba(255,106,0,0.55)] transition-shadow disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                disabled={loading}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </>
                  )}
                </span>
              </motion.button>
            </form>

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center space-y-4 pt-6 mt-6 border-t border-black/10"
            >
              <p className="text-sm text-slate-600">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="text-[#FF6A00] hover:text-orange-300 font-semibold transition-colors duration-300 hover:underline"
                >
                  Get started
                </Link>
              </p>
              <p className="text-xs text-slate-500">
                Having trouble? Contact your site administrator.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}

export default Login;
